import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { putFileToBlobUrl } from '../shared/utils/blob-upload.util';

/** Decisão do cliente sobre uma linha do orçamento. */
export type BudgetLineApproval = 'Pendente' | 'Aprovado' | 'Recusado';

/** Discrimina a linha ao enviar a decisão. */
export type BudgetLineKind = 'servico' | 'item';

export interface BudgetLineDecision {
  tipo: BudgetLineKind;
  id: number;
  decisao: BudgetLineApproval;
  motivo?: string | null;
}

export interface BudgetDecisionResult {
  budgetId: number;
  status: string;
  clientApproved: boolean;
  workSectionStatus: string;
  totalGross: number;
  totalApproved: number;
  totalRejected: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  isFullyDecided: boolean;
  changedCount: number;
  decidedBy?: string | null;
}

export interface BudgetMirrorResult {
  budgetId: number;
  serviceOrderId: number;
  sourceBudgetWorkSectionId: number;
  executionWorkSectionId: number;
  created: boolean;
  updated: boolean;
  servicesCopied: number;
  itemsCopied: number;
  totalApproved: number;
}

export interface BudgetComment {
  id: number;
  mensagem: string;
  autor: string;
  /** Portal (cliente), ERP (equipe) ou System (registro automático). */
  autorOrigem: string;
  interno: boolean;
  linhaTipo?: string | null;
  linhaId?: number | null;
  criadoEm: string;
}

export interface BudgetEvidence {
  id: number;
  fileName: string;
  blobName: string;
  contentType?: string | null;
  fileSize?: number | null;
  tipo: string;
  descricao?: string | null;
  linhaTipo?: string | null;
  linhaId?: number | null;
  autor: string;
  autorOrigem: string;
  criadoEm: string;
  downloadUrl: string;
}

interface EvidenceUploadTarget {
  budgetId: number;
  fileName: string;
  blobName: string;
  uploadUrl: string;
  maxFileSizeBytes: number;
}

/** Envelope padrão das functions do host Order. */
interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

/**
 * Decisões por item, comentários e evidências de um orçamento.
 *
 * Separado do `BudgetServiceOrdersService` (que cuida do CRUD) porque estas
 * operações exigem o autor no header `X-User-Id` e têm ciclo de vida próprio.
 */
@Injectable({ providedIn: 'root' })
export class BudgetCollaborationService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'bugetserviceorderterciaries';

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
  ) {}

  // ===== DECISÕES =====

  decideLines(
    budgetId: number,
    decisoes: BudgetLineDecision[],
    options?: { recordedAt?: string | null; aprovarTudo?: boolean }
  ): Observable<BudgetDecisionResult> {
    const body: Record<string, unknown> = { decisoes };
    if (options?.recordedAt) body['recordedAt'] = options.recordedAt;
    if (options?.aprovarTudo) body['aprovarTudo'] = true;
    return this.http
      .post<ApiEnvelope<BudgetDecisionResult>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/itens/decidir`),
        body,
        { headers: this.getHeaders(true) }
      )
      .pipe(map(res => this.unwrap(res)), catchError(err => this.fail(err)));
  }

  /** Atalho: aprova todas as linhas pendentes com data/hora opcional. */
  approveAll(budgetId: number, recordedAt?: string | null): Observable<BudgetDecisionResult> {
    return this.decideLines(budgetId, [], { aprovarTudo: true, recordedAt });
  }

  /** Cria/atualiza na OS a work section de execução com as linhas aprovadas. */
  mirrorApproved(budgetId: number): Observable<BudgetMirrorResult> {
    return this.http
      .post<ApiEnvelope<BudgetMirrorResult>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/espelhar-aprovado`),
        {},
        { headers: this.getHeaders(true) }
      )
      .pipe(
        map(res => this.mapMirror(this.unwrap(res))),
        catchError(err => this.fail(err))
      );
  }

  // ===== COMENTÁRIOS =====

  listComments(budgetId: number): Observable<BudgetComment[]> {
    return this.http
      .get<ApiEnvelope<BudgetComment[]>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/comentarios`),
        { headers: this.getHeaders() }
      )
      .pipe(
        map(res => (this.unwrap(res) ?? []).map(c => this.mapComment(c))),
        catchError(err => this.fail(err))
      );
  }

  addComment(
    budgetId: number,
    body: { mensagem: string; interno?: boolean; linhaTipo?: string | null; linhaId?: number | null }
  ): Observable<BudgetComment> {
    return this.http
      .post<ApiEnvelope<BudgetComment>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/comentarios`),
        body,
        { headers: this.getHeaders(true) }
      )
      .pipe(map(res => this.mapComment(this.unwrap(res))), catchError(err => this.fail(err)));
  }

  // ===== EVIDÊNCIAS =====

  listEvidences(budgetId: number): Observable<BudgetEvidence[]> {
    return this.http
      .get<ApiEnvelope<BudgetEvidence[]>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/evidencias`),
        { headers: this.getHeaders() }
      )
      .pipe(
        map(res => (this.unwrap(res) ?? []).map(e => this.mapEvidence(e))),
        catchError(err => this.fail(err))
      );
  }

  /**
   * Encadeia os três passos do upload: pedir a URL assinada, enviar o arquivo
   * direto ao blob e registrar os metadados.
   */
  uploadAndRegisterEvidence(
    budgetId: number,
    file: File,
    meta?: { tipo?: string; descricao?: string; linhaTipo?: string | null; linhaId?: number | null }
  ): Observable<BudgetEvidence> {
    const params = new HttpParams().set('fileName', file.name);

    return this.http
      .get<ApiEnvelope<EvidenceUploadTarget>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/evidencias/upload-url`),
        { headers: this.getHeaders(), params }
      )
      .pipe(
        map(res => this.unwrap(res)),
        switchMap(target =>
          putFileToBlobUrl(this.httpBackend, target.uploadUrl, file).pipe(
            switchMap(() =>
              this.http.post<ApiEnvelope<BudgetEvidence>>(
                this.buildUrl(`${this.resourcePath}/${budgetId}/evidencias`),
                {
                  blobName: target.blobName,
                  fileName: target.fileName,
                  contentType: file.type || null,
                  fileSize: file.size,
                  tipo: meta?.tipo ?? null,
                  descricao: meta?.descricao ?? null,
                  linhaTipo: meta?.linhaTipo ?? null,
                  linhaId: meta?.linhaId ?? null
                },
                { headers: this.getHeaders(true) }
              )
            )
          )
        ),
        map(res => this.mapEvidence(this.unwrap(res))),
        catchError(err => this.fail(err))
      );
  }

  deleteEvidence(budgetId: number, evidenceId: number): Observable<void> {
    return this.http
      .delete<ApiEnvelope<unknown>>(
        this.buildUrl(`${this.resourcePath}/${budgetId}/evidencias/${evidenceId}`),
        { headers: this.getHeaders(true) }
      )
      .pipe(map(() => undefined), catchError(err => this.fail(err)));
  }

  private getHeaders(includeUserId: boolean = false): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = { 'Content-Type': 'application/json' };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    const userId = includeUserId ? this.getAuditUserId() : null;
    if (userId) {
      headerMap['X-User-Id'] = userId;
    }

    return new HttpHeaders(headerMap);
  }

  /** Nenhum interceptor injeta o autor; é resolvido aqui como no fluxo de anexos da OS. */
  private getAuditUserId(): string | null {
    const stored = localStorage.getItem('lastLoggedUserId');
    if (stored) return stored;

    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
      const id = payload?.userId ?? payload?.sub ?? payload?.nameid ?? payload?.id;
      return id != null ? String(id) : null;
    } catch {
      return null;
    }
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    const url = `${normalizedBase}/${normalizedPath}`;
    return baseQuery ? `${url}?${baseQuery}` : url;
  }

  private unwrap<T>(res: ApiEnvelope<T> | T): T {
    const envelope = res as ApiEnvelope<T>;
    return (envelope && typeof envelope === 'object' && 'data' in envelope
      ? envelope.data
      : res) as T;
  }

  private mapComment(dto: any): BudgetComment {
    return {
      id: dto?.id ?? dto?.Id ?? 0,
      mensagem: dto?.mensagem ?? dto?.Mensagem ?? '',
      autor: dto?.autor ?? dto?.Autor ?? '—',
      autorOrigem: dto?.autorOrigem ?? dto?.AutorOrigem ?? 'ERP',
      interno: dto?.interno ?? dto?.Interno ?? false,
      linhaTipo: dto?.linhaTipo ?? dto?.LinhaTipo ?? null,
      linhaId: dto?.linhaId ?? dto?.LinhaId ?? null,
      criadoEm: dto?.criadoEm ?? dto?.CriadoEm ?? ''
    };
  }

  private mapEvidence(dto: any): BudgetEvidence {
    return {
      id: dto?.id ?? dto?.Id ?? 0,
      fileName: dto?.fileName ?? dto?.FileName ?? '',
      blobName: dto?.blobName ?? dto?.BlobName ?? '',
      contentType: dto?.contentType ?? dto?.ContentType ?? null,
      fileSize: dto?.fileSize ?? dto?.FileSize ?? null,
      tipo: dto?.tipo ?? dto?.Tipo ?? '',
      descricao: dto?.descricao ?? dto?.Descricao ?? null,
      linhaTipo: dto?.linhaTipo ?? dto?.LinhaTipo ?? null,
      linhaId: dto?.linhaId ?? dto?.LinhaId ?? null,
      autor: dto?.autor ?? dto?.Autor ?? '—',
      autorOrigem: dto?.autorOrigem ?? dto?.AutorOrigem ?? 'ERP',
      criadoEm: dto?.criadoEm ?? dto?.CriadoEm ?? '',
      downloadUrl: dto?.downloadUrl ?? dto?.DownloadUrl ?? ''
    };
  }

  private mapMirror(dto: any): BudgetMirrorResult {
    return {
      budgetId: dto?.budgetId ?? dto?.BudgetId ?? 0,
      serviceOrderId: dto?.serviceOrderId ?? dto?.ServiceOrderId ?? 0,
      sourceBudgetWorkSectionId: dto?.sourceBudgetWorkSectionId ?? dto?.SourceBudgetWorkSectionId ?? 0,
      executionWorkSectionId: dto?.executionWorkSectionId ?? dto?.ExecutionWorkSectionId ?? 0,
      created: dto?.created ?? dto?.Created ?? false,
      updated: dto?.updated ?? dto?.Updated ?? false,
      servicesCopied: dto?.servicesCopied ?? dto?.ServicesCopied ?? 0,
      itemsCopied: dto?.itemsCopied ?? dto?.ItemsCopied ?? 0,
      totalApproved: dto?.totalApproved ?? dto?.TotalApproved ?? 0
    };
  }

  private fail(err: any): Observable<never> {
    const message =
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      'Não foi possível concluir a operação no orçamento.';
    return throwError(() => new Error(message));
  }
}
